import { h, patchChildren } from '@/plugins/saveable/sval';
import store from '@/store/store';
import isObject from 'lodash/isObject';
import isEqual from 'lodash/isEqual';

const getSaveables = (vm, saveable) => (typeof saveable === 'function'
  ? saveable.call(vm)
  : saveable);

const isNested = (saveable) => saveable.isNested();

const getSaveableChildKeys = (saveable) => (isNested(saveable)
  ? Object.keys(saveable.children)
  : []);

const buildFromKeys = (keys, fn, keyFn) => keys.reduce((build, key) => {
  const modifiedBuild = build;
  modifiedBuild[keyFn
    ? keyFn(key)
    : key] = fn(key);
  return modifiedBuild;
}, {});

/* eslint-disable no-underscore-dangle */
let _cachedComponent = null;
const getComponent = (Vue) => {
  if (_cachedComponent) {
    return _cachedComponent;
  }

  const SBase = Vue.extend({
    $store: store,
    computed: {
      refs() {
        const oldVal = this._sval;

        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this._sval = this.children;
        patchChildren(oldVal, this._sval);
        const refs = {};
        if (this._sval !== null) {
          this._sval.forEach((c) => {
            refs[c.key] = c.vm;
          });
        }

        return refs;
      },
    },
    beforeCreate() {
      this._sval = null;
    },
    beforeDestroy() {
      if (this._sval) {
        patchChildren(this._sval);
        this._sval = null;
      }
    },
  });

  const Saveable = SBase.extend({
    data() {
      return {
        saveables: null,
        prop: null,
        rootModel: null,
        isRoot: false,
        lazyParentModel: () => null,
      };
    },
    methods: {
      isNested(key) {
        return isNested(this.saveables.children[key]);
      },
      getRef(key) {
        return this.refs[key];
      },
      refProxy(key) {
        return this.getRef(key).proxy;
      },
      pushChildToArray(saveable) {
        this.saveables.children.push(saveable);
      },
      addChildToIndex(saveable, key, splice) {
        if (splice) {
          this.saveables.children.splice(key, 0, saveable);
        } else {
          this.$set(this.saveables.children, key, saveable);
        }
      },
      addChildToArray(saveable, key, splice) {
        if (key !== undefined) {
          if (Number.isInteger(key) && key <= this.saveables.children.length) {
            this.addChildToIndex(saveable, key, splice);
          }
        } else {
          this.pushChildToArray(saveable);
        }
      },
      addChildToObject(saveable, key) {
        if (key !== undefined) {
          this.addChildToIndex(saveable, key);
        }
      },
      $reset() {
        this.$removeChildIf(() => true);

        Object.keys(this.saveables.originalChildren).forEach((key) => this.$addChild(this.saveables.originalChildren[key], this.saveables.hasArrayOfChildren()
          ? undefined
          : key));

        this.childKeys.forEach((key) => this.refProxy(key).$reset());
      },
      $updateOriginal() {
        this.saveables.updateOriginal();
      },
      $addChild(saveable, key, splice) {
        if (saveable) {
          if (this.saveables.hasArrayOfChildren()) {
            this.addChildToArray(saveable, key, splice);
          } else if (isObject(this.saveables.children) && key !== undefined) {
            this.addChildToObject(saveable, key);
          }
        }
      },
      $addChildOrUpdateModelOfExisting(saveable, key) {
        if (this.childKeys.includes(key)) {
          if (saveable.isLeaf() && this.refProxy(key).$saveable.isLeaf()) {
            this.refProxy(key).$setModelAndUpdate(saveable.value);
          }
        } else {
          this.$addChild(saveable, key);
        }
      },
      $removeChild(key) {
        if (key !== null && key !== undefined) {
          if (this.saveables.children[key]) {
            this.$delete(this.saveables.children, key);
          }
        } else if (this.saveables.hasArrayOfChildren()) {
          this.saveables.children.pop();
        }
      },
      $removeChildIf(predicate) {
        let index = Object.keys(this.saveables.children).length;
        // eslint-disable-next-line no-plusplus
        while (index--) {
          const key = this.childKeys[index];
          if (predicate(this.refProxy(key), index)) {
            if (this.saveables.hasArrayOfChildren()) {
              this.saveables.children.splice(index, 1);
            } else {
              this.$delete(this.saveables.children, key);
            }
          }
        }
      },
      $touch() {
        this.saveables.touchRecursively();
      },
      $setIsLoading(isLoading) {
        this.saveables.setIsLoadingRecursively(isLoading);
      },
      $toggleIsLoading() {
        this.saveables.setIsLoadingRecursively(!this.saveables.isLoading);
      },
      $setIsSaving(isSaving) {
        this.saveables.setIsSavingRecursively(isSaving);
      },
      $toggleIsSaving() {
        this.saveables.setIsSavingRecursively(!this.saveables.isSaving);
      },
      $setDisableValidator(value, recursive = false) {
        this.saveables.setDisableValidator(value, recursive);
      },
      $setDisableHasChanged(value, recursive = false) {
        this.saveables.setDisableHasChanged(value, recursive);
      },
      $setDisableDirty(value, recursive = false) {
        this.saveables.setDisableDirty(value, recursive);
      },
      $iterator() {
        const { refProxy } = this;
        const { childKeys } = this;
        const { length } = this.childKeys;

        let step = 0;
        return {
          next() {
            const iterableObj = { value: undefined, done: true };

            if (step < length) {
              iterableObj.value = refProxy([childKeys[step]]);
              iterableObj.done = false;
            }

            step += 1;

            return iterableObj;
          },
        };
      },
      $toArray() {
        return this.childKeys.map((key) => this.refProxy(key));
      },
      $setMetaProperty(key, value) {
        this.saveables.meta[key] = value;
      },
    },
    computed: {
      nestedChildKeys() {
        return this.childKeys.filter(this.isNested);
      },
      leafChildKeys() {
        return this.childKeys.filter((key) => !this.isNested(key));
      },
      childKeys() {
        return isNested(this.saveables)
          ? Object.keys(this.saveables.children)
          : [];
      },
      proxy() {
        const keyDefs = buildFromKeys(this.childKeys, (key) => ({
          enumerable: true,
          configurable: true,
          get: () => this.refProxy(key),
        }));

        return Object.defineProperties(
          {},
          {
            ...keyDefs,
            // TODO: Remove this debug param!
            $saveable: {
              enumerable: true,
              get: () => this.saveables,
            },
            $isValid: {
              enumerable: true,
              get: () => this.saveables.isValid(),
            },
            $hasChanged: {
              enumerable: true,
              get: () => this.saveables.hasChanged(),
            },
            $isDirty: {
              enumerable: true,
              get: () => this.saveables.isDirty(),
            },
            $hasError: {
              enumerable: true,
              get: () => this.saveables.hasError(),
            },
            $isSaveable: {
              enumerable: true,
              get: () => this.saveables.isSaveable(),
            },
            $isResettable: {
              enumerable: true,
              get: () => this.saveables.isResettable(),
            },
            $meta: {
              enumerable: true,
              get: () => this.saveables.meta,
            },
            $setMetaProperty: {
              enumerable: true,
              get: () => this.$setMetaProperty,
            },
            $reset: {
              enumerable: true,
              get: () => this.$reset,
            },
            $updateOriginal: {
              enumerable: true,
              get: () => this.$updateOriginal,
            },
            $addChild: {
              enumerable: true,
              get: () => this.$addChild,
            },
            $addChildOrUpdateModelOfExisting: {
              enumerable: true,
              get: () => this.$addChildOrUpdateModelOfExisting,
            },
            $removeChild: {
              enumerable: true,
              get: () => this.$removeChild,
            },
            $removeChildIf: {
              enumerable: true,
              get: () => this.$removeChildIf,
            },
            $childKeys: {
              enumerable: true,
              get: () => this.childKeys,
            },
            $touch: {
              enumerable: true,
              get: () => this.$touch,
            },
            [Symbol.iterator]: {
              enumerable: true,
              get: () => this.$iterator,
            },
            $toArray: {
              enumerable: true,
              get: () => this.$toArray,
            },
            $mappedValue: {
              enumerable: true,
              get: () => this.saveables.mappedValue(),
            },
            $mappedChanged: {
              enumerable: true,
              get: () => this.saveables.mappedValue(true),
            },
            $isLoading: {
              enumerable: true,
              get: () => this.saveables.isLoading,
            },
            $isSaving: {
              enumerable: true,
              get: () => this.saveables.isSaving,
            },
            $setIsLoading: {
              enumerable: true,
              get: () => this.$setIsLoading,
            },
            $toggleIsLoading: {
              enumerable: true,
              get: () => this.$toggleIsLoading,
            },
            $setIsSaving: {
              enumerable: true,
              get: () => this.$setIsSaving,
            },
            $toggleIsSaving: {
              enumerable: true,
              get: () => this.$toggleIsSaving,
            },
            $setDisableValidator: {
              enumerable: true,
              get: () => this.$setDisableValidator,
            },
            $setDisableHasChanged: {
              enumerable: true,
              get: () => this.$setDisableHasChanged,
            },
            $setDisableDirty: {
              enumerable: true,
              get: () => this.$setDisableDirty,
            },
            $invalidReason: {
              get: () => this.saveables.getInvalidReason(),
            },
            $validatorResult: {
              enumerable: true,
              get: () => ({
                isDirty: this.saveables.isDirty(),
                hasPassedValidator: this.saveables.hasPassedValidators(),
                hasError: this.saveables.hasError(),
                hasWarning: this.saveables.hasWarning(),
                message: this.proxy.$invalidReason,
              }),
            },
            $hasChildren: {
              enumerable: true,
              get: () => this.childKeys.length > 0,
            },
            $uuid: {
              enumerable: true,
              get: () => this.saveables.uuid,
            },
          },
        );
      },
      children() {
        return [
          /* eslint-disable no-use-before-define */
          ...this.nestedChildKeys.map((key) => renderNested(this, key)),
          ...this.leafChildKeys.map((key) => renderLeaf(this, key)),
          /* eslint-enable no-use-before-define */
        ].filter(Boolean);
      },
    },
  });

  const SaveableLeaf = SBase.extend({
    data() {
      return {
        saveables: null,
        prop: null,
        rootModel: null,
        isRoot: false,
        lazyParentModel: () => null,
      };
    },
    methods: {
      $reset() {
        if (this.proxy.$hasChanged) {
          this.saveables.resetValue();
        }
      },
      $updateOriginal() {
        this.saveables.updateOriginal();
      },
      $touch() {
        this.saveables.touchRecursively();
      },
      $setIsLoading(isLoading) {
        this.saveables.setIsLoadingRecursively(isLoading);
      },
      $toggleIsLoading() {
        this.saveables.setIsLoadingRecursively(!this.saveables.isLoading);
      },
      $setIsSaving(isSaving) {
        this.saveables.setIsSavingRecursively(isSaving);
      },
      $toggleIsSaving() {
        this.saveables.setIsSavingRecursively(!this.saveables.isSaving);
      },
      $setModelAndUpdate(value) {
        this.proxy.$model = value;
        this.$updateOriginal();
      },
      $setValidator(validator) {
        this.saveables.validator = validator;
      },
      $setMetaProperty(key, value) {
        this.saveables.meta[key] = value;
      },
      $setDisableValidator(value) {
        this.saveables.setDisableValidator(value);
      },
      $setDisableHasChanged(value) {
        this.saveables.setDisableHasChanged(value);
      },
      $setDisableDirty(value) {
        this.saveables.setDisableDirty(value);
      },
    },
    computed: {
      proxy() {
        return Object.defineProperties(
          {},
          {
            // TODO: Remove this debug param!
            $saveable: {
              enumerable: true,
              get: () => this.saveables,
            },
            $validator: {
              enumerable: true,
              get: () => {
                if (this.saveables) {
                  return this.saveables.validator;
                }

                return null;
              },
            },
            $isValid: {
              enumerable: true,
              get: () => {
                if (this.saveables) {
                  return this.saveables.isValid();
                }

                return false;
              },
            },
            $invalidReason: {
              enumerable: true,
              get: () => this.saveables.getInvalidReason(),
            },
            $hasChanged: {
              enumerable: true,
              get: () => {
                if (this.saveables) {
                  return this.saveables.hasChanged();
                }

                return false;
              },
            },
            $isSaveable: {
              enumerable: true,
              get: () => this.saveables.isSaveable(),
            },
            $isResettable: {
              enumerable: true,
              get: () => this.saveables.isResettable(),
            },
            $model: {
              enumerable: true,
              get: () => this.saveables.value,
              set: (value) => {
                if (!isEqual(value, this.saveables.value)) {
                  this.saveables.dirty = true;
                }

                this.saveables.value = value;
              },
            },
            $isDirty: {
              enumerable: true,
              get: () => this.saveables.isDirty(),
            },
            $hasError: {
              enumerable: true,
              get: () => this.saveables.hasError(),
            },
            $meta: {
              enumerable: true,
              get: () => this.saveables.meta,
            },
            $setValidator: {
              enumerable: true,
              get: () => this.$setValidator,
            },
            $setMetaProperty: {
              enumerable: true,
              get: () => this.$setMetaProperty,
            },
            $reset: {
              enumerable: true,
              get: () => this.$reset,
            },
            $updateOriginal: {
              enumerable: true,
              get: () => this.$updateOriginal,
            },
            $touch: {
              enumerable: true,
              get: () => this.$touch,
            },
            $mappedValue: {
              enumerable: true,
              get: () => this.saveables.mappedValue(),
            },
            $mappedChanged: {
              enumerable: true,
              get: () => this.saveables.mappedValue(true),
            },
            $isLoading: {
              enumerable: true,
              get: () => this.saveables.isLoading,
            },
            $isSaving: {
              enumerable: true,
              get: () => this.saveables.isSaving,
            },
            $setIsLoading: {
              enumerable: true,
              get: () => this.$setIsLoading,
            },
            $toggleIsLoading: {
              enumerable: true,
              get: () => this.$toggleIsLoading,
            },
            $setIsSaving: {
              enumerable: true,
              get: () => this.$setIsSaving,
            },
            $toggleIsSaving: {
              enumerable: true,
              get: () => this.$toggleIsSaving,
            },
            $setDisableValidator: {
              enumerable: true,
              get: () => this.$setDisableValidator,
            },
            $setDisableHasChanged: {
              enumerable: true,
              get: () => this.$setDisableHasChanged,
            },
            $setDisableDirty: {
              enumerable: true,
              get: () => this.$setDisableDirty,
            },
            $setModelAndUpdate: {
              enumerable: true,
              get: () => this.$setModelAndUpdate,
            },
            $validatorResult: {
              enumerable: true,
              get: () => ({
                isDirty: this.saveables.isDirty(),
                hasPassedValidator: this.saveables.hasPassedValidators(),
                hasError: this.saveables.hasError(),
                hasWarning: this.saveables.hasWarning(),
                message: this.proxy.$invalidReason,
              }),
            },
            $uuid: {
              enumerable: true,
              get: () => this.saveables.uuid,
            },
          },
        );
      },
    },
  });

  const renderNested = (vm, key) => h(
    Saveable,
    key,
    {
      saveables: vm.saveables.children[key],
      lazyParentModel: () => vm,
      prop: key,
      rootModel: vm.rootModel,
    },
  );

  const renderLeaf = (vm, key) => h(
    SaveableLeaf,
    key,
    {
      saveables: vm.saveables.children[key],
      lazyParentModel: () => vm,
      prop: key,
      rootModel: vm.rootModel,
    },
  );

  _cachedComponent = { SaveableLeaf, Saveable, SBase };
  return _cachedComponent;
};

let _cachedVue = null;
function getVue(rootVm) {
  if (_cachedVue) {
    return _cachedVue;
  }

  let Vue = rootVm.constructor;

  while (Vue.super) {
    Vue = Vue.super;
  }
  _cachedVue = Vue;
  return Vue;
}

const validateModel = (model, saveables) => {
  const Vue = getVue(model);
  const { SaveableLeaf, Saveable, SBase } = getComponent(Vue);

  return new SBase({
    data() {
      return {
        saveables,
      };
    },
    methods: {
      pushChildToArray(saveable) {
        this.saveables.children.push(saveable);
      },
      addChildToIndex(saveable, key, splice) {
        if (splice) {
          this.saveables.children.splice(key, 0, saveable);
        } else {
          this.$set(this.saveables.children, key, saveable);
        }
      },
      addChildToArray(saveable, key, splice) {
        if (key !== undefined) {
          if (Number.isInteger(key) && key <= this.saveables.children.length) {
            this.addChildToIndex(saveable, key, splice);
          }
        } else {
          this.pushChildToArray(saveable);
        }
      },
      addChildToObject(saveable, key) {
        if (key !== undefined) {
          this.addChildToIndex(saveable, key);
        }
      },
      $reset() {
        this.$removeChildIf(() => true);

        Object.keys(this.saveables.originalChildren).forEach((key) => this.$addChild(this.saveables.originalChildren[key], this.saveables.hasArrayOfChildren()
          ? undefined
          : key));

        Object.keys(this.refs).forEach((key) => {
          this.refs[key].$reset();
        });
      },
      $updateOriginal() {
        this.saveables.updateOriginal();
      },
      $addChild(saveable, key, splice) {
        if (saveable) {
          if (this.saveables.hasArrayOfChildren()) {
            this.addChildToArray(saveable, key, splice);
          } else if (isObject(this.saveables.children) && key !== undefined) {
            this.addChildToObject(saveable, key);
          }
        }
      },
      $addChildOrUpdateModelOfExisting(saveable, key) {
        if (this.$childKeys.includes(key)) {
          if (saveable.isLeaf() && this.refs[key].proxy.$saveable.isLeaf()) {
            this.refs[key].proxy.$setModelAndUpdate(saveable.value);
          }
        } else {
          this.$addChild(saveable, key);
        }
      },
      $removeChild(key) {
        if (key !== null && key !== undefined) {
          this.$delete(this.saveables.children, key);
        } else if (this.saveables.hasArrayOfChildren()) {
          this.saveables.children.pop();
        }
      },
      $removeChildIf(predicate) {
        let index = Object.keys(this.saveables.children).length;
        // eslint-disable-next-line no-plusplus
        while (index--) {
          const key = this.$childKeys[index];
          if (predicate(this.refs[key].proxy, index)) {
            if (this.saveables.hasArrayOfChildren()) {
              this.saveables.children.splice(index, 1);
            } else {
              this.$delete(this.saveables.children, key);
            }
          }
        }
      },
      $setIsLoading(isLoading) {
        this.saveables.setIsLoadingRecursively(isLoading);
      },
      $toggleIsLoading() {
        this.saveables.setIsLoadingRecursively(!this.saveables.isLoading);
      },
      $setIsSaving(isSaving) {
        this.saveables.setIsSavingRecursively(isSaving);
      },
      $toggleIsSaving() {
        this.saveables.setIsSavingRecursively(!this.saveables.isSaving);
      },
      $setDisableValidator(value, recursive = false) {
        this.saveables.setDisableValidator(value, recursive);
      },
      $setDisableHasChanged(value, recursive = false) {
        this.saveables.setDisableHasChanged(value, recursive);
      },
      $setDisableDirty(value, recursive = false) {
        this.saveables.setDisableDirty(value, recursive);
      },
      $touch() {
        this.saveables.touchRecursively();
      },
      $toArray() {
        return this.$childKeys.map((key) => this.refs[key].proxy);
      },
      $iterator() {
        const { refs } = this;
        const childKeys = this.$childKeys;
        const { length } = this.$childKeys;

        let step = 0;
        return {
          next() {
            const iterableObj = { value: undefined, done: true };

            if (step < length) {
              iterableObj.value = refs[childKeys[step]].proxy;
              iterableObj.done = false;
            }

            step += 1;

            return iterableObj;
          },
        };
      },
      $setMetaProperty(key, value) {
        this.saveables.meta[key] = value;
      },
    },
    computed: {
      $isValid() {
        return this.saveables.isValid();
      },
      $hasChanged() {
        return this.saveables.hasChanged();
      },
      $hasError() {
        return this.saveables.hasError();
      },
      $isDirty() {
        return this.saveables.isDirty();
      },
      $isSaveable() {
        return this.saveables.isSaveable();
      },
      $isResettable() {
        return this.saveables.isResettable();
      },
      $isLoading() {
        return this.saveables.isLoading;
      },
      $isSaving() {
        return this.saveables.isSaving;
      },
      $meta() {
        return this.saveables.meta;
      },
      $childKeys() {
        return getSaveableChildKeys(this.saveables);
      },
      $mappedValue() {
        return this.saveables.mappedValue();
      },
      $mappedChanged() {
        return this.saveables.mappedChanged();
      },
      $invalidReason() {
        return this.saveables.getInvalidReason();
      },
      $validatorResult() {
        return {
          isDirty: this.saveables.isDirty(),
          hasPassedValidator: this.saveables.hasPassedValidators(),
          hasError: this.saveables.hasError(),
          hasWarning: this.saveables.hasWarning(),
          message: this.$invalidReason,
        };
      },
      $hasChildren() {
        return getSaveableChildKeys(this.saveables).length > 0;
      },
      $saveable() {
        return this.saveables;
      },
      $uuid() {
        return this.saveables.uuid;
      },
      children() {
        const childKeys = getSaveableChildKeys(this.saveables);

        if (!childKeys || !childKeys.length) {
          return null;
        }

        const nestedChildKeys = childKeys.filter((key) => isNested(this.saveables.children[key]));
        const leafChildKeys = childKeys.filter((key) => !isNested(this.saveables.children[key]));

        const nestedChildren = nestedChildKeys.map((key) => h(
          Saveable,
          key,
          {
            saveables: this.saveables.children[key],
            prop: key,
            lazyParentModel: () => model,
            rootModel: model,
            isRoot: true,
          },
        ));

        const leafChildren = leafChildKeys.map((key) => h(
          SaveableLeaf,
          key,
          {
            saveables: this.saveables.children[key],
            prop: key,
            lazyParentModel: () => model,
            rootModel: model,
            isRoot: true,
          },
        ));

        return [
          ...nestedChildren,
          ...leafChildren,
        ];
      },
    },
  });
};

const saveableMixin = {
  data() {
    const svbls = getSaveables(this, this.$options.saveables2);
    if (svbls) {
      this._saveables = validateModel(this, svbls);
    }

    return {};
  },
  beforeCreate() {
    const options = this.$options;
    const svbls = options.saveables2;
    if (!svbls) {
      return;
    }

    if (!options.computed) {
      options.computed = {};
    }

    if (options.computed.$s) {
      return;
    }

    // eslint-disable-next-line func-names
    options.computed.$s = function () {
      if (this._saveables) {
        const saveableObj = Object.keys(this._saveables.refs).reduce((obj, key) => {
          const modObj = obj;
          modObj[key] = this._saveables.refs[key].proxy;
          return modObj;
        }, {});

        saveableObj.$isValid = this._saveables.$isValid;
        saveableObj.$hasChanged = this._saveables.$hasChanged;
        saveableObj.$isSaveable = this._saveables.$isSaveable;
        saveableObj.$isResettable = this._saveables.$isResettable;
        saveableObj.$reset = this._saveables.$reset;
        saveableObj.$updateOriginal = this._saveables.$updateOriginal;
        saveableObj.$addChild = this._saveables.$addChild;
        saveableObj.$addChildOrUpdateModelOfExisting = this._saveables.$addChildOrUpdateModelOfExisting;
        saveableObj.$removeChild = this._saveables.$removeChild;
        saveableObj.$removeChildIf = this._saveables.$removeChildIf;
        saveableObj.$meta = this._saveables.$meta;
        saveableObj.$childKeys = this._saveables.$childKeys;
        saveableObj.$mappedValue = this._saveables.$mappedValue;
        saveableObj.$mappedChanged = this._saveables.$mappedChanged;
        saveableObj.$hasError = this._saveables.$hasError;
        saveableObj.$isDirty = this._saveables.$isDirty;
        saveableObj.$touch = this._saveables.$touch;
        saveableObj.$setIsLoading = this._saveables.$setIsLoading;
        saveableObj.$toggleIsLoading = this._saveables.$toggleIsLoading;
        saveableObj.$setIsSaving = this._saveables.$setIsSaving;
        saveableObj.$toggleIsSaving = this._saveables.$toggleIsSaving;
        saveableObj.$isLoading = this._saveables.$isLoading;
        saveableObj.$isSaving = this._saveables.$isSaving;
        saveableObj.$toArray = this._saveables.$toArray;
        saveableObj[Symbol.iterator] = this._saveables.$iterator;
        saveableObj.$setMetaProperty = this._saveables.$setMetaProperty;
        saveableObj.$invalidReason = this._saveables.$invalidReason;
        saveableObj.$validatorResult = this._saveables.$validatorResult;
        saveableObj.$setDisableValidator = this._saveables.$setDisableValidator;
        saveableObj.$setDisableHasChanged = this._saveables.$setDisableHasChanged;
        saveableObj.$setDisableDirty = this._saveables.$setDisableDirty;
        saveableObj.$hasChildren = this._saveables.$hasChildren;
        saveableObj.$saveable = this._saveables.$saveable;
        saveableObj.$uuid = this._saveables.$uuid;

        return saveableObj;
      }

      return null;
    };
  },
  beforeDestroy() {
    if (this._saveables) {
      this._saveables.$destroy();
      this._saveables = null;
    }
  },
};
/* eslint-enable no-underscore-dangle */

function Saveables(Vue) {
  Vue.mixin(saveableMixin);
}

export default Saveables;
